/**
 * Time formatting utilities for the video player
 */

/**
 * Formats seconds into a human-readable time string (HH:MM:SS or MM:SS)
 * @param seconds - The time in seconds
 * @returns Formatted time string
 */
export function formatTime(seconds: number): string {
  if (isNaN(seconds) || seconds === Infinity || seconds < 0) {
    return '--:--'
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Formats a duration (in seconds) to a readable format
 * Alias for formatTime but semantically clearer when used for durations
 * @param duration - The duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(duration: number | null | undefined): string {
  if (!duration || duration <= 0) {
    return '--:--'
  }
  return formatTime(duration)
}

/**
 * Formats a date to a localized string
 * @param date - The date to format
 * @param locale - The locale to use (defaults to browser locale)
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string,
  locale?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  if (isNaN(dateObj.getTime())) {
    return ''
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  }

  return dateObj.toLocaleDateString(locale || undefined, defaultOptions)
}

/**
 * Formats a date to show relative time (e.g., "2 hours ago", "3 days ago")
 * @param date - The date to format
 * @param locale - The locale to use
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date | string, locale?: string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  if (isNaN(dateObj.getTime())) {
    return ''
  }

  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  // Define time intervals in seconds
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60
  }

  // Find the appropriate interval
  for (const [unit, seconds] of Object.entries(intervals)) {
    const interval = Math.floor(diffInSeconds / seconds)
    
    if (interval >= 1) {
      const rtf = new Intl.RelativeTimeFormat(locale || undefined, { numeric: 'auto' })
      return rtf.format(-interval, unit as Intl.RelativeTimeFormatUnit)
    }
  }

  // Less than a minute ago
  const rtf = new Intl.RelativeTimeFormat(locale || undefined, { numeric: 'auto' })
  return rtf.format(-diffInSeconds, 'second')
}

/**
 * Calculates the remaining time from current position to duration
 * @param currentTime - Current playback time in seconds
 * @param duration - Total duration in seconds
 * @returns Formatted remaining time string
 */
export function formatRemainingTime(currentTime: number, duration: number): string {
  if (!duration || duration <= 0 || currentTime < 0) {
    return '--:--'
  }
  
  const remaining = Math.max(0, duration - currentTime)
  return formatTime(remaining)
}

/**
 * Converts time string (HH:MM:SS or MM:SS) to seconds
 * @param timeString - Time string to parse
 * @returns Time in seconds
 */
export function parseTimeString(timeString: string): number {
  const parts = timeString.split(':').map(part => parseInt(part, 10))
  
  if (parts.length === 3) {
    // HH:MM:SS
    const [hours, minutes, seconds] = parts
    return hours * 3600 + minutes * 60 + seconds
  } else if (parts.length === 2) {
    // MM:SS
    const [minutes, seconds] = parts
    return minutes * 60 + seconds
  } else if (parts.length === 1) {
    // SS
    return parts[0]
  }
  
  return 0
}

/**
 * Formats seconds to show only the most significant unit
 * E.g., "2h", "45m", "30s"
 * @param seconds - Time in seconds
 * @returns Compact time string
 */
export function formatCompactTime(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) {
    return '0s'
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}h`
  } else if (minutes > 0) {
    return `${minutes}m`
  } else {
    return `${secs}s`
  }
}

/**
 * Gets a percentage of progress
 * @param current - Current value
 * @param total - Total value
 * @returns Percentage (0-100)
 */
export function getProgressPercent(current: number, total: number): number {
  if (!total || total <= 0 || current < 0) {
    return 0
  }
  return Math.min(100, Math.max(0, (current / total) * 100))
}